import { useQuery, useMutation } from '@apollo/client'
import { Trans } from '@lingui/macro'
import { useGlobals } from 'context'
import filter from 'lodash/filter'
import React, { useContext, useEffect, useState } from 'react'
import { CHANGE_USER_SETTINGS } from '../mutations'
import { GET_USER_SETTINGS } from '../queries'

const UserSettingContext = React.createContext()

function formatSetting(setting) {
  return {
    key: setting.setting_key,
    value: setting.setting_value,
    changed: false,
  }
}

function formatSettings(settings) {
  let formattedSettings = {}
  settings.forEach((setting) => {
    const key = setting.setting_key.split(' ').join('_')
    formattedSettings = {
      ...formattedSettings,
      [key]: formatSetting(setting),
    }
  })
  return formattedSettings
}

function UserSettingProvider({ children }) {
  const { createError } = useGlobals()
  const [userSettings, setUserSettings] = useState([])
  const { loading, data, refetch } = useQuery(GET_USER_SETTINGS)
  const { userSettings: cachedSettings = [] } = data || {}

  const settingsCreation = (cache, { data: { changeUserSettings } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_USER_SETTINGS,
      })
      cache.writeQuery({
        query: GET_USER_SETTINGS,
        data: {
          userSettings: [...cached.userSettings, ...changeUserSettings],
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const [newUserSettings] = useMutation(CHANGE_USER_SETTINGS, {
    update: settingsCreation,
  })
  const settingsUpdate = (cache, { data: { changeUserSettings } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_USER_SETTINGS,
      })
      cache.writeQuery({
        query: GET_USER_SETTINGS,
        data: {
          userSettings: cached.userSettings.map((setting) => {
            const changeSetting =
              changeUserSettings.find(
                (_setting) => _setting.setting_key === setting.setting_key,
              ) || null
            if (changeSetting !== null) {
              return changeSetting
            }
            return setting
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const [changeUserSettings] = useMutation(CHANGE_USER_SETTINGS, {
    update: settingsUpdate,
  })

  useEffect(() => {
    if (!loading && cachedSettings.length > 0) {
      setUserSettings(formatSettings(cachedSettings))
    }
  }, [loading, cachedSettings])

  async function createUserSettings(settings) {
    try {
      const changedSettings = filter(settings, (setting, key) => {
        return setting.changed === true
      })
      const input = changedSettings.map((setting) => {
        return {
          setting_key: setting.key,
          setting_value: setting.value,
        }
      })
      if (input.length > 0) {
        await newUserSettings({
          variables: {
            input,
          },
        })
      }
    } catch (error) {
      createError(error, <Trans>Settings</Trans>, 'create')
    }
  }
  async function updateUserSettings(settings) {
    try {
      const changedSettings = filter(settings, (setting, key) => {
        return setting.changed === true
      })
      const input = changedSettings.map((setting) => {
        return {
          setting_key: setting.key,
          setting_value: setting.value,
        }
      })
      if (input.length > 0) {
        await changeUserSettings({
          variables: {
            input,
          },
        })
      }
    } catch (error) {
      createError(error, <Trans>Settings</Trans>, 'update')
    }
  }

  return (
    <UserSettingContext.Provider
      value={{
        loading,
        refetch,
        userSettings,
        createUserSettings,
        updateUserSettings,
      }}
    >
      {children}
    </UserSettingContext.Provider>
  )
}

function useUserSettings() {
  return useContext(UserSettingContext)
}

export { UserSettingProvider, useUserSettings }
